import React, { useMemo, useState } from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { Layers, Plus, X, Trash2, Maximize2, LineChart, AreaChart, AlertTriangle } from 'lucide-react';
import Modal from './Modal';
import api from '../api/axios';
import { eventAnnotations } from './MonitoringDashboard';

interface Reading {
  date: string;
  [key: string]: any;
}

interface Dataset {
  id: string;
  label: string;
  data: Reading[];
  metadata?: {
    unit?: string;
    selectedColumns?: string[];
    [key: string]: any;
  };
}

// Uma série de uma visualização conjunta: coluna de um sensor do projeto.
interface ViewSeries {
  dataset_id: string;
  column: string;
}

interface CombinedView {
  id: string;
  titulo: string;
  chart_type: 'line' | 'area';
  series: ViewSeries[];
  created_at?: string;
}

interface CombinedVisualizationProps {
  projectId: string;
  datasets: Dataset[];
  views: CombinedView[];
  eventos?: any[];
  canManage: boolean;
  onChange: () => void;
  onError: (message: string) => void;
}

// Paletas categóricas validadas (banda de luminosidade OKLCH, separação CVD
// entre pares adjacentes e contraste com a superfície) para os fundos reais
// dos cartões: branco no modo claro e slate-800 no escuro. A cor segue a
// posição da série na visualização, por isso é estável entre renders.
export const LIGHT_PALETTE = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16', '#ec4899'];
export const DARK_PALETTE = ['#3b82f6', '#059669', '#d97706', '#ef4444', '#8b5cf6', '#0891b2', '#65a30d', '#ec4899'];
const MAX_SERIES = LIGHT_PALETTE.length;

// Colunas numéricas de um sensor (as escolhidas no upload, ou inferidas dos dados).
const datasetColumns = (ds: Dataset): string[] =>
  ds.metadata?.selectedColumns || (ds.data?.[0] ? Object.keys(ds.data[0]).filter(k => k !== 'date') : []);

const CombinedVisualization: React.FC<CombinedVisualizationProps> = ({ projectId, datasets, views, eventos = [], canManage, onChange, onError }) => {
  const [showBuilder, setShowBuilder] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [chartType, setChartType] = useState<'line' | 'area'>('line');
  const [selected, setSelected] = useState<ViewSeries[]>([]);
  const [saving, setSaving] = useState(false);
  const [expandedView, setExpandedView] = useState<CombinedView | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<CombinedView | null>(null);

  const isDarkMode = document.documentElement.classList.contains('dark');
  const palette = isDarkMode ? DARK_PALETTE : LIGHT_PALETTE;

  const datasetById = useMemo(() => {
    const map: { [id: string]: Dataset } = {};
    datasets.forEach(ds => { map[ds.id] = ds; });
    return map;
  }, [datasets]);

  // Sensores em tempo real nunca têm "data" persistido no projeto (ficam só no buffer
  // ao vivo do browser) - combiná-los aqui daria sempre uma série vazia. Ficam de fora
  // da escolha; se um dia deixarem de ser tempo real, voltam a aparecer normalmente.
  const combinableDatasets = useMemo(
    () => datasets.filter(ds => ds.metadata?.sourceType !== 'realtime'),
    [datasets]
  );

  // Constrói as séries ApexCharts de uma visualização; ignora séries de sensores
  // entretanto apagados (missing) para a caixa continuar a funcionar.
  const buildSeries = (viewSeries: ViewSeries[]) => {
    const series: { name: string; data: { x: number; y: any }[] }[] = [];
    const units: string[] = [];
    let missing = 0;
    viewSeries.forEach(s => {
      const ds = datasetById[s.dataset_id];
      if (!ds) { missing += 1; return; }
      series.push({
        name: `${ds.label} · ${s.column}`,
        data: (ds.data || []).map(d => ({ x: new Date(d.date).getTime(), y: d[s.column] ?? null })),
      });
      units.push(ds.metadata?.unit || '');
    });
    return { series, units, missing };
  };

  const chartOptions = (type: 'line' | 'area', units: string[], compact: boolean): ApexOptions => ({
    chart: {
      type,
      toolbar: compact
        ? { show: false }
        : { show: true, autoSelected: 'zoom', tools: { download: true, selection: true, zoom: true, zoomin: true, zoomout: true, pan: true, reset: true } },
      zoom: { enabled: !compact, type: 'x', autoScaleYaxis: true },
      animations: { enabled: false },
      background: 'transparent',
      foreColor: isDarkMode ? '#94a3b8' : '#64748b',
    },
    colors: palette,
    dataLabels: { enabled: false },
    // Eventos do projeto (campanhas, intervenções) como linhas verticais.
    annotations: { xaxis: eventAnnotations(eventos, isDarkMode) },
    stroke: { curve: 'smooth', width: 2.5, lineCap: 'round' },
    fill: type === 'area'
      ? { type: 'gradient', gradient: { shadeIntensity: 0, opacityFrom: 0.35, opacityTo: 0.05, stops: [0, 100] } }
      : { type: 'solid', opacity: 1 },
    xaxis: {
      type: 'datetime',
      tooltip: { enabled: false },
      labels: {
        style: { fontWeight: 700, colors: isDarkMode ? '#94a3b8' : '#64748b' },
        datetimeUTC: false,
        datetimeFormatter: { year: 'yyyy', month: "MMM 'yy", day: 'dd MMM', hour: 'HH:mm' },
      },
      axisBorder: { show: true, color: isDarkMode ? '#334155' : '#e2e8f0' },
      axisTicks: { show: true, color: isDarkMode ? '#334155' : '#e2e8f0' },
    },
    yaxis: {
      labels: {
        style: { fontWeight: 800, colors: [isDarkMode ? '#94a3b8' : '#64748b'] },
        formatter: (val) => (val == null ? '' : Number(val).toLocaleString('pt-PT', { maximumFractionDigits: 1 })),
      },
      forceNiceScale: true,
    },
    grid: {
      borderColor: isDarkMode ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.05)',
      yaxis: { lines: { show: true } },
      xaxis: { lines: { show: false } },
      padding: { top: 0, right: 12, bottom: 0, left: 8 },
    },
    tooltip: {
      theme: isDarkMode ? 'dark' : 'light',
      shared: true,
      intersect: false,
      x: { format: 'dd MMM yyyy HH:mm' },
      style: { fontSize: '12px' },
      // Cada série mantém a sua unidade (sensores diferentes podem medir grandezas diferentes).
      y: { formatter: (val, opts) => (val == null ? '—' : `${val.toFixed(2)} ${units[opts?.seriesIndex ?? 0] || ''}`.trim()) },
    },
    markers: { size: 0, strokeWidth: 2, hover: { size: 5 }, strokeColors: isDarkMode ? '#0b0f1a' : '#fff' },
    // Etiquetas da legenda em cor de texto neutra (a identidade vem do marcador colorido).
    legend: { position: 'bottom', horizontalAlign: 'center', fontWeight: 600, offsetY: 4, labels: { useSeriesColors: false }, markers: { strokeWidth: 0 } as any },
    theme: { mode: isDarkMode ? 'dark' : 'light' },
  });

  const isSelected = (dsId: string, col: string) => selected.some(s => s.dataset_id === dsId && s.column === col);

  const toggleSeries = (dsId: string, col: string) => {
    if (isSelected(dsId, col)) {
      setSelected(prev => prev.filter(s => !(s.dataset_id === dsId && s.column === col)));
    } else if (selected.length < MAX_SERIES) {
      setSelected(prev => [...prev, { dataset_id: dsId, column: col }]);
    }
  };

  const closeBuilder = () => {
    setShowBuilder(false);
    setTitulo('');
    setChartType('line');
    setSelected([]);
  };

  const handleCreate = async () => {
    if (!selected.length) return;
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/views`, {
        titulo: titulo.trim() || 'Visualização conjunta',
        chart_type: chartType,
        series: selected,
      });
      closeBuilder();
      onChange();
    } catch (err: any) {
      onError(err.response?.data?.detail || 'Erro ao guardar a visualização.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (view: CombinedView) => {
    setConfirmDelete(null);
    try {
      await api.delete(`/projects/${projectId}/views/${view.id}`);
      onChange();
    } catch (err: any) {
      onError(err.response?.data?.detail || 'Erro ao apagar a visualização.');
    }
  };

  const preview = useMemo(() => buildSeries(selected), [selected, datasetById]);

  // Sem visualizações e sem permissão para criar: a secção não aparece.
  if (!views.length && !canManage) return null;
  // Sem sensores (não em tempo real) não há nada para combinar.
  if (!combinableDatasets.length) return null;

  return (
    <div className="bg-white dark:bg-slate-800 p-6 sm:p-8 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-sm transition-colors">
      {/* Cabeçalho da secção */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3 min-w-0">
          <div className="p-2 bg-indigo-600/10 text-indigo-600 rounded-xl shrink-0"><Layers size={18} /></div>
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white tracking-tight leading-tight">Visualização conjunta</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">Compara séries de vários sensores no mesmo gráfico</p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-xs px-2.5 py-0.5 rounded-full font-semibold bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300">{views.length}</span>
          {canManage && (
            <button
              onClick={() => setShowBuilder(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-all active:scale-95 text-sm font-semibold"
            >
              <Plus size={16} /> <span>Nova visualização</span>
            </button>
          )}
        </div>
      </div>

      {/* Cartões das visualizações guardadas */}
      {views.length === 0 ? (
        <div className="border border-dashed border-slate-200 dark:border-slate-700 rounded-2xl p-10 text-center">
          <Layers className="mx-auto text-slate-300 dark:text-slate-600 mb-3" size={32} />
          <p className="text-slate-500 dark:text-slate-400 font-medium text-sm">Ainda não há visualizações conjuntas.</p>
          <p className="text-xs text-slate-400 mt-1">Cria uma para comparar sensores lado a lado.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {views.map(view => {
            const { series, units, missing } = buildSeries(view.series);
            return (
              <div key={view.id} className="bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-200 dark:border-slate-700 p-5 transition-colors">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <h4 className="text-sm font-semibold text-slate-900 dark:text-white truncate" title={view.titulo}>{view.titulo}</h4>
                    <p className="text-xs text-slate-400 mt-0.5">{series.length} série{series.length !== 1 ? 's' : ''}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {series.length > 0 && (
                      <button onClick={() => setExpandedView(view)} className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-white dark:hover:bg-slate-800 rounded-lg transition-all" title="Expandir">
                        <Maximize2 size={15} />
                      </button>
                    )}
                    {canManage && (
                      <button onClick={() => setConfirmDelete(view)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-white dark:hover:bg-slate-800 rounded-lg transition-all" title="Apagar visualização">
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                </div>

                {missing > 0 && (
                  <div className="flex items-center gap-2 text-[11px] font-medium text-amber-600 dark:text-amber-500 mb-2">
                    <AlertTriangle size={12} /> {missing} série{missing !== 1 ? 's' : ''} de sensor{missing !== 1 ? 'es' : ''} já apagado{missing !== 1 ? 's' : ''}
                  </div>
                )}

                {series.length > 0 ? (
                  <Chart key={view.chart_type} options={chartOptions(view.chart_type, units, true)} series={series} type={view.chart_type} height={260} />
                ) : (
                  <div className="h-[260px] flex items-center justify-center text-sm text-slate-400">Sem séries disponíveis.</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal de criação com pré-visualização */}
      {showBuilder && (
        <div className="fixed inset-0 z-[2600] flex items-center justify-center p-4 bg-[#05070a]/90 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-white dark:bg-[#161b26] rounded-3xl border border-slate-200 dark:border-slate-800 max-w-4xl w-full max-h-[92vh] overflow-hidden shadow-2xl animate-in zoom-in duration-300 flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-slate-100 dark:border-slate-800 shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-indigo-600/10 text-indigo-600 rounded-xl"><Layers size={18} /></div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white tracking-tight">Nova visualização conjunta</h3>
                  <p className="text-xs text-slate-400">Escolhe as séries, vê a pré-visualização e adiciona ao projeto</p>
                </div>
              </div>
              <button onClick={closeBuilder} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"><X size={20} className="text-slate-400" /></button>
            </div>

            <div className="p-6 overflow-y-auto custom-scrollbar space-y-5">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[220px]">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Título</label>
                  <input
                    type="text"
                    value={titulo}
                    onChange={(e) => setTitulo(e.target.value)}
                    placeholder="Ex.: Temperaturas FBG vs. movimento da fissura"
                    className="w-full p-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 font-medium text-slate-800 dark:text-white placeholder:font-normal placeholder:text-slate-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Tipo de gráfico</label>
                  <div className="flex items-center bg-slate-50 dark:bg-slate-900 rounded-xl p-1 border border-slate-200 dark:border-slate-700">
                    <button onClick={() => setChartType('line')} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${chartType === 'line' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'}`}>
                      <LineChart size={15} /> Linha
                    </button>
                    <button onClick={() => setChartType('area')} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${chartType === 'area' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'}`}>
                      <AreaChart size={15} /> Área
                    </button>
                  </div>
                </div>
              </div>

              {/* Seleção de séries, agrupadas por sensor */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Séries ({selected.length}/{MAX_SERIES})</label>
                  {selected.length >= MAX_SERIES && <span className="text-[11px] font-medium text-amber-600 dark:text-amber-500">Máximo de {MAX_SERIES} séries por visualização</span>}
                </div>
                <div className="space-y-3">
                  {combinableDatasets.map(ds => {
                    const cols = datasetColumns(ds);
                    if (!cols.length) return null;
                    return (
                      <div key={ds.id} className="p-3.5 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-100 dark:border-slate-800">
                        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2 truncate" title={ds.label}>
                          {ds.label}
                          {ds.metadata?.unit ? <span className="font-normal text-slate-400"> · {ds.metadata.unit}</span> : null}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {cols.map(col => {
                            const active = isSelected(ds.id, col);
                            const colorIdx = selected.findIndex(s => s.dataset_id === ds.id && s.column === col);
                            const disabled = !active && selected.length >= MAX_SERIES;
                            return (
                              <button
                                key={col}
                                onClick={() => toggleSeries(ds.id, col)}
                                disabled={disabled}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                                  active
                                    ? 'bg-white dark:bg-slate-800 border-indigo-400 dark:border-indigo-500 text-slate-800 dark:text-white shadow-sm'
                                    : disabled
                                      ? 'border-slate-100 dark:border-slate-800 text-slate-300 dark:text-slate-600 cursor-not-allowed'
                                      : 'border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-indigo-300 dark:hover:border-indigo-700'
                                }`}
                              >
                                {active && <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: palette[colorIdx % palette.length] }} />}
                                {col}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Pré-visualização */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Pré-visualização</label>
                {preview.series.length > 0 ? (
                  <div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-100 dark:border-slate-800">
                    {/* key força a remontagem ao trocar o tipo — o ApexCharts nem sempre converte line<->area in-place */}
                    <Chart key={chartType} options={chartOptions(chartType, preview.units, true)} series={preview.series} type={chartType} height={280} />
                  </div>
                ) : (
                  <div className="h-[160px] border border-dashed border-slate-200 dark:border-slate-700 rounded-2xl flex flex-col items-center justify-center gap-2 text-slate-400">
                    <Layers size={24} className="text-slate-300 dark:text-slate-600" />
                    <p className="text-xs font-medium">Seleciona pelo menos uma série para pré-visualizar.</p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-5 border-t border-slate-100 dark:border-slate-800 shrink-0">
              <button onClick={closeBuilder} className="px-5 py-2.5 text-sm font-medium text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors">Cancelar</button>
              <button
                onClick={handleCreate}
                disabled={!selected.length || saving}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus size={16} /> <span>{saving ? 'A adicionar…' : 'Adicionar'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Vista expandida de uma visualização */}
      {expandedView && (() => {
        const { series, units } = buildSeries(expandedView.series);
        return (
          <div className="fixed inset-0 z-[2600] flex items-center justify-center p-4 bg-[#05070a]/90 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="bg-white dark:bg-[#161b26] rounded-3xl border border-slate-200 dark:border-slate-800 max-w-6xl w-full overflow-hidden shadow-2xl animate-in zoom-in duration-300 flex flex-col" style={{ height: '85vh' }}>
              <div className="flex items-center justify-between p-5 border-b border-slate-100 dark:border-slate-800 shrink-0">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 bg-indigo-600/10 text-indigo-600 rounded-xl shrink-0"><Layers size={18} /></div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">{expandedView.titulo}</p>
                    <p className="text-[11px] text-slate-400">{series.length} série{series.length !== 1 ? 's' : ''} · zoom e pan disponíveis</p>
                  </div>
                </div>
                <button onClick={() => setExpandedView(null)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors shrink-0"><X size={20} className="text-slate-400" /></button>
              </div>
              <div className="flex-1 p-5 min-h-0">
                <Chart options={chartOptions(expandedView.chart_type, units, false)} series={series} type={expandedView.chart_type} height="100%" />
              </div>
            </div>
          </div>
        );
      })()}

      <Modal
        isOpen={!!confirmDelete}
        title="Apagar visualização"
        message={`Tens a certeza que queres apagar "${confirmDelete?.titulo}"? Os dados dos sensores não são afetados.`}
        type="danger"
        onConfirm={() => confirmDelete && handleDelete(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
      />
    </div>
  );
};

export default CombinedVisualization;
